'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuthContext } from '@/components/providers/AuthProvider';
import { createClient } from '@/lib/supabase/browser';
import NavbarAuth from '@/components/NavbarAuth';
import OnboardingBanner from '@/components/OnboardingBanner';
import SuspensionBanner from '@/components/SuspensionBanner';
import Footer from '@/components/Footer';

export default function AuthedLayout({ children }: { children: React.ReactNode }) {
  const { user, loading, signOut } = useAuthContext();
  const router = useRouter();
  const [moderationChecked, setModerationChecked] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      router.push('/login');
      return;
    }
    if (!user.email_confirmed_at) {
      router.replace('/onboarding');
      return;
    }
    // Sign-in block check: if an admin set sign_in_blocked, sign the user
    // out and bounce to a notice page. Wrapped in try/catch so a transient
    // network failure doesn't trap the user.
    let cancelled = false;
    (async () => {
      try {
        const supabase = createClient();
        const { data } = await supabase
          .from('profiles')
          .select('sign_in_blocked, deletion_scheduled_at')
          .eq('id', user.id)
          .maybeSingle();
        if (cancelled) return;
        if (data?.sign_in_blocked || data?.deletion_scheduled_at) {
          await signOut().catch(() => {});
          router.replace('/login?blocked=1');
          return;
        }
        setModerationChecked(true);
      } catch {
        // Fail open — don't trap the user on a network blip.
        setModerationChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, loading, router, signOut]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-0">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen">
      <NavbarAuth />
      <main className="min-h-screen bg-surface-0 text-text-primary pt-[calc(48px+env(safe-area-inset-top))] pb-[64px] md:pb-0 md:pl-[72px]">
        <SuspensionBanner />
        <OnboardingBanner />
        {children}
      </main>
      <Footer />
    </div>
  );
}
